import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  BookOpen,
  MessageSquare,
  Menu,
  Wand2,
  Sparkles,
} from "lucide-react";
import { cn } from "@foundation/altzero_base/base/lib/utils";
import { Badge } from "@foundation/altzero_base/base/components/ui/badge";
import { supabase } from "../utils/supabaseClient";

const navigationItems = [
  {
    name: "Dashboard",
    href: "/scopingai/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Proposals",
    href: "/scopingai/proposals",
    icon: FileText,
  },
  {
    name: "Resource Library",
    href: "/scopingai/resource-library",
    icon: BookOpen,
    children: [
      {
        name: "Templates",
        href: "/scopingai/resource-library/documents",
        icon: FileText,
      },

      {
        name: "Prompts",
        href: "/scopingai/resource-library/prompts",
        icon: MessageSquare,
      },
      {
        name: "Scoping Knowledge Base",
        href: "/scopingai/resource-library/scoping-knowledge-base",
        icon: BookOpen,
      },
    ],
  },
];

export default function ScopingAISidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState<boolean | null>(null);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(true);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsOnboarded(true);
          setIsLoadingOnboarding(false);
          return;
        }

        const { data: profile, error } = await supabase
          .from('base_profiles')
          .select('is_onboarded')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setIsOnboarded(true); // Default to true to avoid blocking
        } else {
          setIsOnboarded(!!profile?.is_onboarded);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setIsOnboarded(true); // Default to true to avoid blocking
      } finally {
        setIsLoadingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const isActive = (href: string) => {
    if (href === "/scopingai/dashboard") {
      return (
        location.pathname === "/scopingai" ||
        location.pathname === "/scopingai/dashboard"
      );
    }
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  };

  const isChildActive = (parentHref: string, childHref: string) => {
    return location.pathname === childHref;
  };

  return (
    <div
      className={cn(
        "bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out no-print",
        sidebarCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <Link
            to="/scopingai/dashboard"
            className="flex items-center space-x-3"
          >
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-xl font-bold text-gray-900">ScopingAI</h1>
                <p className="text-sm text-gray-600">AI-Powered Scoping</p>
              </div>
            )}
          </Link>
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Setup Wizard Button - Only show if not onboarded */}
      {!isLoadingOnboarding && !isOnboarded && (
        <div className="px-4 py-3 border-b border-gray-200">
          <button
            onClick={() => navigate('/scopingai/setup-wizard')}
            className={cn(
              "w-full relative overflow-hidden rounded-xl p-4 bg-gradient-to-r from-primary via-accent-primary to-primary text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl group",
              sidebarCollapsed && "p-3"
            )}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-accent-primary/80 to-primary/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-center gap-2">
              {sidebarCollapsed ? (
                <Wand2 className="w-5 h-5 mx-auto" />
              ) : (
                <>
                  <div className="relative">
                    <Sparkles className="w-5 h-5 animate-pulse" />
                    <div className="absolute inset-0 bg-white/20 rounded-full blur-md animate-ping" />
                  </div>
                  <span className="text-sm flex-1">Start Setup Wizard</span>
                  <Badge className="bg-white/20 text-white border-white/30 text-xs">
                    New
                  </Badge>
                </>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {navigationItems.map((item) => (
          <div key={item.name}>
            <Link
              to={item.href}
              className={cn(
                "flex items-center px-1 md:px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                sidebarCollapsed ? "justify-center" : "space-x-3",
                isActive(item.href)
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-gray-700 hover:bg-gray-100"
              )}
              title={sidebarCollapsed ? item.name : undefined}
            >
              <item.icon className="h-5 w-5" />
              {!sidebarCollapsed && <span>{item.name}</span>}
            </Link>

            {/* Sub-navigation - only show when not collapsed */}
            {!sidebarCollapsed && item.children && isActive(item.href) && (
              <div className="ml-8 mt-2 space-y-1">
                {item.children.map((child) => (
                  <Link
                    key={child.name}
                    to={child.href}
                    className={cn(
                      "flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                      isChildActive(item.href, child.href)
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <child.icon className="h-4 w-4" />
                    <span>{child.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}
